import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Wallet as WalletIcon, Gift, ArrowDownToLine, ExternalLink, RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/* Tap IN TV — Creator/User Wallet
 *
 * Single page that combines:
 *  • A balance card (USD + points)
 *  • Gift-card redeem
 *  • Stripe Connect Express onboarding + cashout
 *  • A ledger of recent activity
 */
export default function Wallet() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cashoutAmt, setCashoutAmt] = useState("10.00");
  const [params] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/wallet/me");
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // If we just got back from Stripe onboarding, refresh status
  useEffect(() => {
    if (params.get("stripe") === "return") {
      api.post("/wallet/stripe/refresh").then(() => load()).catch(() => {});
    }
  }, [params, load]);

  const w = data?.wallet;
  const ledger = data?.ledger || [];
  const minCents = data?.cashout_min_cents || 1000;

  const redeem = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/wallet/giftcards/redeem", { code: code.trim() });
      toast.success(`Redeemed +$${(r.data.credited.cash_cents / 100).toFixed(2)} & ${r.data.credited.points} pts`);
      setCode("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const startStripeOnboarding = async () => {
    setBusy(true);
    try {
      const origin = window.location.origin;
      const r = await api.post("/wallet/stripe/onboard", { origin });
      window.location.href = r.data.url;
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };

  const refreshStripe = async () => {
    setBusy(true);
    try {
      await api.post("/wallet/stripe/refresh");
      await load();
      toast.success("Stripe status refreshed");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const cashout = async () => {
    const cents = Math.round(Number(cashoutAmt) * 100);
    if (!cents || cents < minCents) { toast.error(`Minimum cashout is $${(minCents / 100).toFixed(2)}`); return; }
    setBusy(true);
    try {
      const r = await api.post("/wallet/cashout", { amount_cents: cents });
      toast.success(`Sent $${(r.data.amount_cents / 100).toFixed(2)} to your bank`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="pt-28 text-center text-white/60">Loading wallet…</div>;
  if (!w) return <div className="pt-28 text-center text-white/60">No wallet yet</div>;

  return (
    <div data-testid="wallet-page" className="pt-24 pb-24 max-w-4xl mx-auto px-4 sm:px-6">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-tv-accent font-bold flex items-center gap-2"><WalletIcon className="w-3.5 h-3.5" /> Tap IN Wallet</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl text-white tracking-tighter mt-1">{user?.name?.split(" ")[0] || "Your"} balance</h1>
        <p className="text-white/60 mt-1 text-sm">Cash credit and points from gift cards, promotions, referrals, and tips.</p>
      </div>

      {/* Balance card */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div data-testid="wallet-cash-card" className="rounded-2xl bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600 p-5 text-white shadow-xl">
          <div className="text-xs uppercase tracking-[0.25em] font-black opacity-90">Cash balance</div>
          <div className="font-display font-black text-5xl mt-2 tabular-nums" data-testid="wallet-cash-balance">${(w.cash_cents / 100).toFixed(2)}</div>
          <div className="text-xs mt-2 opacity-80">Withdraw to your bank via Stripe</div>
        </div>
        <div data-testid="wallet-points-card" className="rounded-2xl bg-gradient-to-br from-pink-500 via-fuchsia-500 to-purple-600 p-5 text-white shadow-xl">
          <div className="text-xs uppercase tracking-[0.25em] font-black opacity-90">Points</div>
          <div className="font-display font-black text-5xl mt-2 tabular-nums" data-testid="wallet-points-balance">{w.points.toLocaleString()}</div>
          <div className="text-xs mt-2 opacity-80">Spend in the Gift Shop · 1 pt = $0.01 store credit</div>
        </div>
      </div>

      {/* Redeem gift card */}
      <section className="rounded-2xl tv-glass p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Gift className="w-5 h-5 text-tv-accent" />
          <h2 className="font-display font-black text-xl text-white">Redeem gift card</h2>
        </div>
        <div className="flex gap-2">
          <input
            data-testid="wallet-giftcard-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="GIFT-XXXX-XXXX-XXXX"
            className="flex-1 bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white placeholder-white/30 outline-none focus:border-tv-accent uppercase tracking-wider font-mono text-sm"
          />
          <button
            data-testid="wallet-giftcard-redeem"
            onClick={redeem}
            disabled={busy || !code.trim()}
            className="bg-tv-accent hover:bg-yellow-400 text-black font-bold px-4 py-2 rounded-lg disabled:opacity-50"
          >Redeem</button>
        </div>
      </section>

      {/* Stripe Connect / Cashout */}
      <section className="rounded-2xl tv-glass p-5 mb-6" data-testid="wallet-cashout-section">
        <div className="flex items-center gap-2 mb-3">
          <ArrowDownToLine className="w-5 h-5 text-tv-accent" />
          <h2 className="font-display font-black text-xl text-white">Cash out to bank</h2>
        </div>
        {!w.stripe_account_id ? (
          <>
            <div className="text-white/70 text-sm mb-3">Connect a Stripe account to receive payouts. Stripe handles all bank-account collection and ID verification.</div>
            <button
              data-testid="wallet-stripe-onboard"
              onClick={startStripeOnboarding}
              disabled={busy}
              className="bg-[#635BFF] hover:bg-[#7066ff] text-white font-bold px-4 py-2.5 rounded-lg inline-flex items-center gap-2 disabled:opacity-50"
            ><ExternalLink className="w-4 h-4" /> Connect with Stripe</button>
          </>
        ) : !w.payouts_enabled ? (
          <>
            <div className="bg-amber-500/10 border border-amber-400/30 text-amber-200 rounded-lg p-3 text-sm mb-3 inline-flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> Stripe is verifying your account. This usually takes a few minutes.
            </div>
            <div className="flex gap-2">
              <button data-testid="wallet-stripe-refresh" onClick={refreshStripe} disabled={busy} className="bg-white/10 hover:bg-white/20 text-white font-bold px-3 py-2 rounded-lg inline-flex items-center gap-2 disabled:opacity-50">
                <RefreshCw className="w-4 h-4" /> Refresh status
              </button>
              <button onClick={startStripeOnboarding} disabled={busy} className="bg-white/10 hover:bg-white/20 text-white font-semibold px-3 py-2 rounded-lg text-sm">Resume onboarding</button>
            </div>
          </>
        ) : (
          <>
            <div className="bg-emerald-500/10 border border-emerald-400/30 text-emerald-200 rounded-lg p-3 text-sm mb-3 inline-flex items-center gap-2" data-testid="wallet-stripe-ready">
              <CheckCircle2 className="w-4 h-4" /> Stripe is ready — withdraw any time.
            </div>
            <div className="flex gap-2 items-center">
              <span className="text-white/60 text-sm">$</span>
              <input
                data-testid="wallet-cashout-amount"
                inputMode="decimal"
                value={cashoutAmt}
                onChange={(e) => setCashoutAmt(e.target.value.replace(/[^0-9.]/g, ""))}
                className="w-32 bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white outline-none focus:border-tv-accent text-right tabular-nums"
              />
              <button
                data-testid="wallet-cashout-submit"
                onClick={cashout}
                disabled={busy}
                className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-4 py-2 rounded-lg inline-flex items-center gap-2 disabled:opacity-50"
              ><ArrowDownToLine className="w-4 h-4" /> Withdraw</button>
            </div>
            <div className="text-white/40 text-xs mt-2">Min ${(minCents / 100).toFixed(2)} · Funds arrive in 1–2 business days</div>
          </>
        )}
      </section>

      {/* Ledger */}
      <section className="rounded-2xl tv-glass p-5">
        <h2 className="font-display font-black text-xl text-white mb-3">Recent activity</h2>
        {ledger.length === 0 ? (
          <div className="text-white/50 text-sm py-3" data-testid="wallet-ledger-empty">No transactions yet.</div>
        ) : (
          <ul data-testid="wallet-ledger" className="divide-y divide-white/5">
            {ledger.map((l) => (
              <li key={l.id} className="py-3 flex items-center gap-3">
                <span className={`shrink-0 w-2 h-2 rounded-full ${l.cash_cents > 0 || l.points > 0 ? "bg-emerald-400" : "bg-rose-400"}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">{l.description || l.kind}</div>
                  <div className="text-[11px] uppercase tracking-wider text-white/40">{new Date(l.created_at).toLocaleString()} · {l.kind}</div>
                </div>
                <div className="shrink-0 text-right tabular-nums">
                  {l.cash_cents !== 0 && <div className={l.cash_cents > 0 ? "text-emerald-300 font-bold" : "text-rose-300 font-bold"}>{l.cash_cents > 0 ? "+" : ""}${(l.cash_cents / 100).toFixed(2)}</div>}
                  {l.points !== 0 && <div className={`text-xs ${l.points > 0 ? "text-pink-300" : "text-rose-300"}`}>{l.points > 0 ? "+" : ""}{l.points} pts</div>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="mt-6 text-center text-white/40 text-xs">
        Need help? <Link to="/support" className="underline hover:text-white">Contact support</Link>
      </div>
    </div>
  );
}
